// Интеграция с MAX Mini Apps (мессенджер MAX, max.ru).
//
// MAX, как и Telegram, прокидывает в страницу мини-приложения мост:
// глобальный объект `window.WebApp` (НЕ `window.Telegram.WebApp` — у MAX
// он лежит прямо на window). Из него берём `initData` — подписанную строку
// для серверной валидации. Шлём её заголовком `X-Max-Init-Data` (у Telegram —
// `X-Telegram-Init-Data`; см. getInitDataHeaderName в miniApp.ts).
//
// Док: https://dev.max.ru/docs/webapps/bridge
//
// Важно: в отличие от Telegram (клиент сам инжектит window.Telegram.WebApp),
// мост MAX создаётся ТОЛЬКО скриптом max-web-app.js. Поэтому подключаем его
// глобально с `defer` в public/index.html — иначе window.WebApp не появится к
// холодному старту и мини-апп не распознается (курица-яйцо: раньше скрипт
// грузился только ПОСЛЕ детекции). Ложных срабатываний на обычном вебе нет:
// детект идёт по НЕПУСТОМУ initData (его подписывает только хост MAX), а не по
// самому факту наличия моста. initData доступен синхронно, без ready() (см. док).
/** URL официального MAX Web App bridge — на случай ручной/динамической загрузки. */
const MAX_WEBAPP_SCRIPT_URL = "https://st.max.ru/js/max-web-app.js";
/** Объект MAX WebApp API, если доступен. */
export const getMaxWebApp = () => {
    if (typeof window !== "undefined" && window.WebApp) {
        return window.WebApp;
    }
    return null;
};
/**
 * СТРУКТУРНАЯ проверка, что строка — подписанная MAX init_data ровно в том
 * виде, как её валидирует бэкенд (verify_max_init_data: parse_qsl → должны быть
 * отдельные пары `hash`/`auth_date`/`user`, `hash` — hex, `user` — JSON с
 * числовым `id`). Возвращает ту же строку, если валидна, иначе null.
 *
 * Именно структурная, а НЕ подстрочная: подстрока `hash=` встречается в
 * произвольных URL (роуты, redirect-параметры), из-за чего голая проверка
 * `includes("hash=")` дала бы ложный MAX-контекст на обычном вебе и сломала бы
 * cookie-флоу. Здесь строка должна РАСПАРСИТЬСЯ в валидные пары.
 */
function asSignedInitData(candidate) {
    if (!candidate)
        return null;
    const params = new URLSearchParams(candidate);
    const hash = params.get("hash");
    const authDate = params.get("auth_date");
    const user = params.get("user");
    if (!hash || !/^[a-f0-9]{16,}$/i.test(hash))
        return null;
    if (!authDate || !/^\d+$/.test(authDate))
        return null;
    if (!user)
        return null;
    try {
        const parsed = JSON.parse(user);
        if (!parsed || typeof parsed.id !== "number")
            return null;
    }
    catch {
        return null;
    }
    return candidate;
}
/**
 * Привести значение к подписанной init_data, пробуя несколько глубин url-декода.
 * MAX кладёт строку в URL обёрнутой (`WebAppData=<encoded>`), а точная глубина
 * кодирования не документирована и в коде трактуется по-разному (ср.
 * shareDeepLink.ts делает лишний decode). Поэтому декодируем пошагово и
 * принимаем ПЕРВУЮ глубину, на которой строка структурно валидна — так работает
 * и для одинарного, и для двойного кодирования, не переразбивая значения.
 */
function coerceSignedInitData(raw) {
    let cur = raw ?? "";
    for (let i = 0; i < 3 && cur; i++) {
        const ok = asSignedInitData(cur);
        if (ok)
            return ok;
        let next;
        try {
            next = decodeURIComponent(cur);
        }
        catch {
            break;
        }
        if (next === cur)
            break;
        cur = next;
    }
    return null;
}
/**
 * MAX кладёт данные запуска в URL миниаппа (query и/или hash), а мост
 * max-web-app.js разбирает их в window.WebApp.initData и ПОДЧИЩАЕТ hash. Под
 * каким ключом лежит подписанная init_data, MAX точно не документирует (см.
 * shareDeepLink.ts) — поэтому пробуем известные имена (`WebAppData` и вариации),
 * а на каждом кандидате прогоняем структурную валидацию с перебором глубины
 * декода (coerceSignedInitData). Ключ-агностичный перебор произвольных
 * параметров НЕ делаем — сузили бы поверхность ложного MAX-контекста на вебе.
 */
const MAX_INIT_DATA_URL_KEYS = [
    "WebAppData",
    "max_init_data",
    "maxInitData",
    "web_app_data",
];
function extractMaxInitDataFromParams(params) {
    for (const key of MAX_INIT_DATA_URL_KEYS) {
        const ok = coerceSignedInitData(params.get(key));
        if (ok)
            return ok;
    }
    return null;
}
/**
 * Прочитать подписанную MAX init_data из launch-URL (query/hash), НЕ полагаясь
 * на мост. Нужно, потому что мост может быть медленным/недоступным или уже
 * подчистить hash — а подписанная строка физически приходит в URL. null — если
 * валидных launch-данных MAX в URL нет (обычный веб).
 */
export function readMaxInitDataFromLaunchUrl() {
    if (typeof window === "undefined")
        return null;
    const fromQuery = extractMaxInitDataFromParams(new URLSearchParams(window.location.search));
    if (fromQuery)
        return fromQuery;
    const fromHash = extractMaxInitDataFromParams(new URLSearchParams(window.location.hash.replace(/^#/, "")));
    if (fromHash)
        return fromHash;
    // Бэйр-вариант: сам hash уже является подписанной строкой (без обёртки-ключа).
    // Только hash — MAX кладёт launch-данные именно туда; query как «голую»
    // init_data не проверяем (это самая крафтящаяся поверхность на вебе).
    const bareHash = coerceSignedInitData(window.location.hash.replace(/^#/, ""));
    if (bareHash)
        return bareHash;
    return null;
}
/**
 * Снимок MAX init_data, сделанный СРАЗУ при загрузке модуля — до первого рендера
 * React, любых редиректов и до того, как мост подчистит launch-hash. Критично
 * для авторизации: без снимка гонка «мост не готов / hash затёрт» уводила MAX в
 * гостевую сессию (пользователь видел кнопки входа вместо своих песен). Берём
 * живой мост, если он уже наполнен, иначе — из URL. Значение в рамках запуска не
 * меняется, поэтому кэшируем один раз (по аналогии с INITIAL_LAUNCH_PARAM в
 * shareDeepLink.ts).
 */
const INITIAL_MAX_INIT_DATA = (() => {
    if (typeof window === "undefined")
        return null;
    const bridge = window.WebApp?.initData;
    if (typeof bridge === "string" && bridge.trim().length > 0)
        return bridge;
    return readMaxInitDataFromLaunchUrl();
})();
/**
 * Запущены ли мы внутри MAX Mini App.
 *
 * Признак — НЕПУСТОЙ подписанный `initData` MAX. Источник — мост
 * `window.WebApp.initData` ИЛИ launch-URL/ранний снимок (см. getMaxInitData):
 * подписанную строку MAX кладёт и в URL запуска, и на мост, только внутри
 * клиента MAX. Мост подключён глобально в index.html, поэтому `window.WebApp`
 * есть и на обычном вебе — но там initData пустой и launch-данных в URL нет, так
 * что ложных срабатываний нет. Проверять просто `Boolean(window.WebApp)` нельзя
 * — это ловило бы веб. Доступна тестовая подмена через localStorage.
 */
export const isMaxMiniApp = () => {
    if (typeof window === "undefined") {
        return false;
    }
    // Тестовый флаг (по аналогии с isTelegramMiniApp).
    try {
        if (localStorage.getItem("isMaxMiniApp") === "true") {
            return true;
        }
    }
    catch {
        /* приватный режим — игнорируем */
    }
    // Подписанный initData есть только внутри клиента MAX (мост / launch-URL).
    return getMaxInitData() != null;
};
/**
 * Загрузить скрипт MAX WebApp-моста, если его ещё нет. Вызывать только когда
 * уже знаем, что мы внутри MAX (см. isMaxMiniApp) — глобально не подключаем.
 */
export const loadMaxWebAppScript = () => {
    return new Promise((resolve, reject) => {
        if (typeof window === "undefined") {
            reject(new Error("Window is not available"));
            return;
        }
        if (window.WebApp) {
            resolve();
            return;
        }
        const existing = document.querySelector(`script[src="${MAX_WEBAPP_SCRIPT_URL}"]`);
        if (existing) {
            existing.addEventListener("load", () => resolve());
            existing.addEventListener("error", () => reject(new Error("Failed to load MAX WebApp script")));
            // Если скрипт уже загружен — резолвим в следующем тике.
            if (window.WebApp)
                resolve();
            return;
        }
        const script = document.createElement("script");
        script.src = MAX_WEBAPP_SCRIPT_URL;
        script.async = true;
        script.onload = () => {
            // Ждём инициализации window.WebApp.
            let waited = 0;
            const timer = setInterval(() => {
                if (window.WebApp) {
                    clearInterval(timer);
                    resolve();
                }
                else if ((waited += 100) >= 3000) {
                    clearInterval(timer);
                    reject(new Error("MAX WebApp script loaded but WebApp is missing"));
                }
            }, 100);
        };
        script.onerror = () => reject(new Error("Failed to load MAX WebApp script"));
        document.head.appendChild(script);
    });
};
/**
 * Получить initData из MAX — сырую подписанную строку для серверной валидации
 * (НЕ initDataUnsafe). Источники по приоритету:
 *  1) живой мост `window.WebApp.initData` — точная строка, если мост наполнен;
 *  2) ранний снимок с загрузки модуля (до того как мост подчистит launch-hash);
 *  3) живое чтение launch-URL (если URL ещё цел).
 * Раньше читали ТОЛЬКО мост — и если он был медленным/недоступным или hash уже
 * затёрт, авторизация MAX срывалась и создавалась гостевая сессия. Теперь MAX
 * не зависит от готовности моста. null — не MAX (обычный веб) / нет данных.
 */
export const getMaxInitData = () => {
    const bridge = getMaxWebApp()?.initData;
    if (typeof bridge === "string" && bridge.trim().length > 0) {
        return bridge;
    }
    if (INITIAL_MAX_INIT_DATA) {
        return INITIAL_MAX_INIT_DATA;
    }
    return readMaxInitDataFromLaunchUrl();
};
/**
 * Ждёт, пока MAX WebApp проинициализируется и initData станет доступен.
 */
export const waitForMaxInitData = async (maxWaitTime = 3000, checkInterval = 100) => {
    const startTime = Date.now();
    while (Date.now() - startTime < maxWaitTime) {
        const initData = getMaxInitData();
        if (initData) {
            return initData;
        }
        await new Promise((resolve) => setTimeout(resolve, checkInterval));
    }
    return null;
};
